import { cfgToThemeContentPath } from '@infinitro/config';
import { supportChatWarn } from '../debug';
/** Max article shortcuts on the Support Chat home screen. */
export const SUPPORT_CHAT_HOME_FEATURED_MAX = 3;
export function subtreeLeafArticleCount(node) {
    if (!node.children?.length)
        return node.articleSlug ? 1 : 0;
    return node.children.reduce((sum, child) => sum + subtreeLeafArticleCount(child), 0);
}
/** Count shown next to rows: prefers live leaf tally; falls back to `articleCount` when subtree not expanded. */
export function displayListedArticleTotal(node) {
    const counted = subtreeLeafArticleCount(node);
    if (counted > 0)
        return counted;
    if (typeof node.articleCount === 'number')
        return node.articleCount;
    return node.articleSlug ? 1 : 0;
}
function isArticleTreeManifest(value) {
    return (value !== null &&
        typeof value === 'object' &&
        typeof value.version === 'number' &&
        Array.isArray(value.nodes));
}
function localizedNodeTitle(node, localeCode) {
    if (localeCode === 'en')
        return node.title;
    return node.languages?.[localeCode]?.trim() || node.title;
}
function localizeNodeTitles(node, localeCode) {
    return {
        ...node,
        title: localizedNodeTitle(node, localeCode),
        children: node.children?.map((child) => localizeNodeTitles(child, localeCode))
    };
}
function localizeManifestTitles(manifest, localeCode) {
    return {
        ...manifest,
        nodes: manifest.nodes.map((node) => localizeNodeTitles(node, localeCode))
    };
}
async function fetchArticleTree() {
    const url = cfgToThemeContentPath('/support-chat/tree.json');
    const response = await fetch(url);
    if (!response.ok)
        return null;
    let data;
    try {
        data = await response.json();
    }
    catch {
        return null;
    }
    return isArticleTreeManifest(data) ? data : null;
}
/**
 * Loads the support chat navigation tree from CDN.
 * The manifest is shared across locales; per-locale labels come from each node's `languages` map.
 */
export async function loadSupportChatArticleTree(localeCode) {
    const primary = normalizeLocale(localeCode);
    try {
        const manifest = await fetchArticleTree();
        if (manifest)
            return localizeManifestTitles(manifest, primary);
    }
    catch {
        // handled below
    }
    supportChatWarn('Support chat article tree CDN fetch failed', {
        treeUrl: cfgToThemeContentPath('/support-chat/tree.json')
    });
    return { version: 1, nodes: [] };
}
export function normalizeLocale(raw) {
    const trimmed = raw?.trim() || '';
    if (!trimmed)
        return 'en';
    return trimmed.toLowerCase().replace(/-/g, '_').split('_')[0] ?? 'en';
}
export function findArticleLeafBySlug(roots, articleSlug) {
    const normalizedSlug = articleSlug.trim();
    if (!normalizedSlug)
        return null;
    let found = null;
    const walk = (node) => {
        if (found)
            return;
        const slug = node.articleSlug?.trim();
        if (slug === normalizedSlug) {
            found = { id: node.id, title: node.title, articleSlug: slug };
            return;
        }
        node.children?.forEach(walk);
    };
    roots.forEach(walk);
    return found;
}
/** Collects leaves with numeric `featuredRank` >= 1 (favorites for home). Sorted by rank ascending, then id. */
function pickLeavesByFeaturedRank(roots) {
    const candidates = [];
    const walk = (node) => {
        node.children?.forEach(walk);
        if (node.children?.length)
            return;
        const slug = node.articleSlug?.trim();
        if (!slug)
            return;
        const rank = node.featuredRank;
        if (typeof rank !== 'number' || !Number.isFinite(rank) || rank < 1)
            return;
        candidates.push({
            rank,
            leaf: { id: node.id, title: node.title, articleSlug: slug }
        });
    };
    roots.forEach(walk);
    candidates.sort((a, b) => a.rank !== b.rank ? a.rank - b.rank : a.leaf.id.localeCompare(b.leaf.id));
    return candidates.map((c) => c.leaf);
}
/**
 * Home shortcuts: use `featuredRank` on leaves in the loaded tree (CDN), up to
 * {@link SUPPORT_CHAT_HOME_FEATURED_MAX}.
 */
export function resolveSupportChatHomeLeaves(roots) {
    return pickLeavesByFeaturedRank(roots).slice(0, SUPPORT_CHAT_HOME_FEATURED_MAX);
}
